import axios from 'axios';
import { toast } from 'sonner';
import { secureStorage } from './secureStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

/** Congregación en contexto (el admin global la cambia desde el menú). */
let currentChurchId = null;
export const setApiChurchId = (churchId) => { currentChurchId = churchId || null; };

api.interceptors.request.use((config) => {
  const token = secureStorage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get' && currentChurchId) {
    config.params = { ...(config.params || {}) };
    if (config.params.church_id === undefined || config.params.church_id === null) {
      config.params.church_id = currentChurchId;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      secureStorage.clear();
      window.location.href = '/login';
    }
    // 402 = suscripción con más de 20 días de atraso: la Suite queda en solo lectura.
    if (error.response?.status === 402 && !window.location.pathname.startsWith('/billing')) {
      toast.error(error.response?.data?.detail || 'Suscripción vencida', {
        action: { label: 'Pagar', onClick: () => { window.location.href = '/billing'; } },
      });
    }
    return Promise.reject(error);
  }
);

export default api;

/** Hub error message ready to show in a toast. */
export const hubError = (error, fallback = 'Ocurrió un error') => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || String(d)).join(' · ') || fallback;
  if (detail && typeof detail === 'object') return detail.msg || fallback;
  return detail || fallback;
};

// Auth
export const authAPI = {
  login: (identifier, password) => api.post('/api/auth/login', { identifier, password }),
  getMe: () => api.get('/api/auth/me'),
  logout: () => api.post('/api/auth/logout'),
};

// Dashboard
export const dashboardAPI = {
  getStats: (churchId) => api.get('/api/dashboard/stats', { params: { church_id: churchId } }),
  faithStats: () => api.get('/api/dashboard/faith-stats'),
};

// Churches
export const churchAPI = {
  list: (search, params) => api.get('/api/churches', { params: { search, limit: 200, ...params } }),
  get: (id) => api.get(`/api/churches/${id}`),
  stats: (id) => api.get(`/api/churches/${id}/stats`),
  update: (id, data) => api.put(`/api/churches/${id}`, data),
  inviteLink: (id) => api.get(`/api/churches/${id}/invite-link`),
  rotateInviteLink: (id) => api.post(`/api/churches/${id}/invite-link/rotate`),
  inviteQr: (id, format) => api.get(`/api/churches/${id}/invite-qr`, {
    params: { format }, responseType: 'blob'
  }),
};

// Members
export const membersAPI = {
  list: (churchId, status, search, params) => api.get('/api/members', {
    params: { church_id: churchId, status, search, limit: 200, ...params }
  }),
  get: (id) => api.get(`/api/members/${id}`),
  approve: (id) => api.post(`/api/members/${id}/approve`),
  reject: (id) => api.post(`/api/members/${id}/reject`),
  remove: (id) => api.delete(`/api/members/${id}`),
};

// Events
export const eventsAPI = {
  list: (upcoming, params) => api.get('/api/events', { params: { upcoming, limit: 200, ...params } }),
  get: (id) => api.get(`/api/events/${id}`),
  create: (data) => api.post('/api/events', data),
  update: (id, data) => api.put(`/api/events/${id}`, data),
  delete: (id) => api.delete(`/api/events/${id}`),
};

// Announcements
export const announcementsAPI = {
  list: (active, params) => api.get('/api/announcements', { params: { active, limit: 200, ...params } }),
  create: (data) => api.post('/api/announcements', data),
  update: (id, data) => api.put(`/api/announcements/${id}`, data),
  delete: (id) => api.delete(`/api/announcements/${id}`),
};

// Posts (noticias)
export const postsAPI = {
  list: (search, params) => api.get('/api/posts', { params: { search, limit: 200, ...params } }),
  create: (data) => api.post('/api/posts', data),
  update: (id, data) => api.put(`/api/posts/${id}`, data),
  delete: (id) => api.delete(`/api/posts/${id}`),
};

// Ministries
export const ministriesAPI = {
  list: (params) => api.get('/api/ministries', { params: { limit: 200, ...params } }),
  create: (data) => api.post('/api/ministries', data),
  update: (id, data) => api.put(`/api/ministries/${id}`, data),
  delete: (id) => api.delete(`/api/ministries/${id}`),
  members: (id) => api.get(`/api/ministries/${id}/members`),
  addMember: (id, userId) => api.post(`/api/ministries/${id}/members/${userId}`),
  removeMember: (id, userId) => api.delete(`/api/ministries/${id}/members/${userId}`),
};

// Groups (comunidad)
export const groupsAPI = {
  list: (params) => api.get('/api/groups', { params: { limit: 200, ...params } }),
  create: (data) => api.post('/api/groups', data),
  update: (id, data) => api.put(`/api/groups/${id}`, data),
  delete: (id) => api.delete(`/api/groups/${id}`),
  members: (id) => api.get(`/api/groups/${id}/members`),
  addMember: (id, userId) => api.post(`/api/groups/${id}/members/${userId}`),
  removeMember: (id, userId) => api.delete(`/api/groups/${id}/members/${userId}`),
};

// Courses + lessons
export const coursesAPI = {
  list: (params) => api.get('/api/courses', { params: { limit: 200, ...params } }),
  get: (id) => api.get(`/api/courses/${id}`),
  create: (data) => api.post('/api/courses', data),
  update: (id, data) => api.put(`/api/courses/${id}`, data),
  delete: (id) => api.delete(`/api/courses/${id}`),
  lessons: (id) => api.get(`/api/courses/${id}/lessons`),
  createLesson: (id, data) => api.post(`/api/courses/${id}/lessons`, data),
  updateLesson: (lessonId, data) => api.put(`/api/lessons/${lessonId}`, data),
  deleteLesson: (lessonId) => api.delete(`/api/lessons/${lessonId}`),
};

// Contributions
export const contributionsAPI = {
  list: (params) => api.get('/api/contributions', { params: { limit: 200, ...params } }),
  summary: (churchId) => api.get('/api/contributions/summary', { params: { church_id: churchId } }),
  get: (id) => api.get(`/api/contributions/${id}`),
  createManual: (data) => api.post('/api/contributions/manual', data),
  update: (id, data) => api.patch(`/api/contributions/${id}`, data),
  history: (id) => api.get(`/api/contributions/${id}/history`),
};

// Wallet
export const walletAPI = {
  get: (churchId) => api.get('/api/wallet', { params: { church_id: churchId } }),
  updateDispersion: (data, churchId) => api.put('/api/wallet/dispersion', data, { params: { church_id: churchId } }),
};

// Prayers (devocionales)
export const prayersAPI = {
  list: (params) => api.get('/api/prayers', { params: { limit: 200, ...params } }),
  create: (data) => api.post('/api/prayers', data),
  delete: (id) => api.delete(`/api/prayers/${id}`),
};

// Petitions
export const petitionsAPI = {
  list: (scope, params) => api.get('/api/petitions', { params: { scope, limit: 200, ...params } }),
  get: (id) => api.get(`/api/petitions/${id}`),
  create: (data) => api.post('/api/petitions', data),
  respond: (id, response) => api.post(`/api/petitions/${id}/respond`, { response }),
  delete: (id) => api.delete(`/api/petitions/${id}`),
};

// Mentorships
export const mentorshipsAPI = {
  list: (status, params) => api.get('/api/mentorships', { params: { status, limit: 200, ...params } }),
  end: (id) => api.post(`/api/mentorships/${id}/end`),
};

// Moderation
export const moderationAPI = {
  reports: (status, params) => api.get('/api/moderation/reports', { params: { status, limit: 200, ...params } }),
  counts: () => api.get('/api/moderation/reports/counts'),
  resolve: (id, action, resolutionNote) => api.post(`/api/moderation/reports/${id}/resolve`, {
    action, resolution_note: resolutionNote
  }),
};

// Team (colaboradores)
export const teamAPI = {
  catalog: () => api.get('/api/team/catalog'),
  list: () => api.get('/api/team'),
  setPermissions: (memberId, permissions) => api.put(`/api/team/${memberId}/permissions`, { permissions }),
};

// Activity log (Hub)
export const activityAPI = {
  list: (action) => api.get('/api/activity-log', { params: { action, limit: 100 } }),
};

// Live events (webhooks del Hub recibidos por la Suite)
export const liveAPI = {
  events: (since) => api.get('/api/hub/events', { params: { since } }),
  webhooks: (churchId) => api.get('/api/hub/webhooks', { params: { church_id: churchId } }),
  diagnostics: () => api.get('/api/hub/webhook-diagnostics'),
  setSecret: (secret) => api.put('/api/hub/webhook-secret', { secret }),
  testSecret: () => api.post('/api/hub/webhook-secret/test'),
};

// Subida de archivos (proxy al Hub → Cloudinary)
export const uploadsAPI = {
  image: (file, onProgress, folder = 'posts') => {
    const data = new FormData();
    data.append('file', file);
    data.append('folder', folder);
    return api.post('/api/uploads/image', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / (e.total || 1))),
    });
  },
  video: (file, onProgress, folder = 'posts') => {
    const data = new FormData();
    data.append('file', file);
    data.append('folder', folder);
    return api.post('/api/uploads/video', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / (e.total || 1))),
    });
  },
};

// Centro de Ayuda (base de conocimiento)
export const kbAPI = {
  list: (params = {}) => api.get('/api/kb/articles', { params }),
  get: (slug) => api.get(`/api/kb/articles/${slug}`),
  create: (data) => api.post('/api/kb/articles', data),
  update: (slug, data) => api.patch(`/api/kb/articles/${slug}`, data),
  remove: (slug) => api.delete(`/api/kb/articles/${slug}`),
  export: () => api.get('/api/kb/export'),
};

// Comunidad Tu Iglesia (congregación propia de la plataforma)
export const comunidadAPI = {
  config: () => api.get('/api/comunidad/config'),
  updateConfig: (data) => api.put('/api/comunidad/config', data),
  ministries: () => api.get('/api/comunidad/ministries'),
  createMinistry: (data) => api.post('/api/comunidad/ministries', data),
  updateMinistry: (id, data) => api.put(`/api/comunidad/ministries/${id}`, data),
  deleteMinistry: (id) => api.delete(`/api/comunidad/ministries/${id}`),
  publishMinistry: (id) => api.post(`/api/comunidad/ministries/${id}/publish`),
};

// Agente de contenido del feed de Comunidad Tu Iglesia
export const contenidoAPI = {
  list: (params = {}) => api.get('/api/comunidad/contenido', { params }),
  generate: (data) => api.post('/api/comunidad/contenido/generar', data),
  update: (id, data) => api.put(`/api/comunidad/contenido/${id}`, data),
  remove: (id) => api.delete(`/api/comunidad/contenido/${id}`),
  publish: (id) => api.post(`/api/comunidad/contenido/${id}/publicar`),
};

// BOMM (gestión externa de tickets de soporte)
export const bommAPI = {
  config: () => api.get('/api/bomm/admin/config'),
  rotateCredentials: () => api.post('/api/bomm/admin/credentials'),
  setWebhookUrl: (webhook_url) => api.put('/api/bomm/admin/webhook-url', { webhook_url }),
  test: () => api.post('/api/bomm/admin/test'),
};

// Interactions (telemetría propia de la Suite)
export const interactionsAPI = {
  track: (data) => api.post('/api/interactions', data),
  trackBatch: (events) => api.post('/api/interactions/batch', { events }),
  summary: (churchId, period, entityType) => api.get('/api/interactions/summary', {
    params: { church_id: churchId, period, entity_type: entityType }
  }),
  topContent: (churchId, entityType, period, limit) => api.get('/api/interactions/top-content', {
    params: { church_id: churchId, entity_type: entityType, period, limit }
  }),
  detail: (entityType, entityId, churchId, period) => api.get(`/api/interactions/detail/${entityType}/${entityId}`, {
    params: { church_id: churchId, period }
  }),
  badges: (entityType, entityIds, churchId) => api.get('/api/interactions/badges', {
    params: { entity_type: entityType, entity_ids: entityIds.join(','), church_id: churchId }
  }),
};
